import type { ReactNode } from 'react';
import {
  areFieldsDirty,
  buildAllFieldsFromSections,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  Card,
  CardContent,
  Form,
  FormBuilderSections,
  getResolver,
  LoadingCard,
  QuoteDerogationLockNotice,
  resolveFieldShortcuts,
  resolveSectionsFieldShortcuts,
  toast,
  useStepperContext,
} from '@folio/components';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Asset,
  BrokerageFeeOnPolicy,
  clientWithFork,
  configControllerGetPublicProductConfigQueryOptions,
  CustomFieldDto,
  derogationControllerCheckDerogationRequirementQueryOptions,
  EditPolicyInputBody,
  getNewBranchIdAfterFork,
  keyValueFieldsToObject,
  useAssetControllerEditAsset,
  usePolicyControllerEditPolicy,
  usePolicyControllerQuotePolicy,
  usePolicyControllerSetPolicyFees,
  usePolicyControllerUpdatePolicyQuote,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { useCurrentFirmType } from '@folio/utils/useCurrentFirmType';
import { FieldShortcut, FormSections, FormSectionsWithShortcuts } from 'korint-sdk/react';
import { toPayloadFields, useEntityFieldConfig } from '../shared-fields/use-entity-fields';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { QuoteRecapModule } from '../QuoteRecapModule/QuoteRecapModule';
import { PricingTierSelect } from '../PricingFunnelModule/PricingTierSelect';
import { FleetTierTable } from '../FleetQuoteModule/FleetTierTable';
import {
  fleetSelectionDraftKey,
  useFleetSelectionDraft,
} from '../FleetQuoteModule/use-fleet-selection-draft';
import { MAX_FLEET_SIZE } from '@folio/constants';
import { useSavePricingSelection } from '../PricingFunnelModule/use-save-pricing-selection';
import { QuoteContext } from '../shared-quote/QuoteContext';
import { useAvailablePricing } from '../shared-quote/use-available-pricing';
import { useComputePricing } from '../shared-quote/use-compute-pricing';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useCurrentActiveAsset } from '@folio/utils/useCurrentActiveAsset';
import {
  shouldDisableQuoteFields,
  isQuoteDerogationApproved,
} from '@folio/utils/quoteDerogationLock';
import { PerilOptionsCard } from '../shared-quote/PerilOptionsCard';
import {
  type ManualComponentFormMap,
  type ManualComponentsGroup,
  ManualComponentsCard,
  fromSavedManualComponents,
  isComponentEditableBy,
  toManualComponentPayload,
  useManualComponentValueTypes,
  useManualComponentsConfig,
} from '../shared-quote/ManualComponentsCard';
import { DocumentsUploadPolicyModule } from '../DocumentsUploadPolicyModule/DocumentsUploadPolicyModule';
import { WaitingForQuoteCard } from '../LegacyQuoteFunnelModule/WaitingForQuoteCard';
import { Stack } from '@folio/components/ui/flex';

type QuoteFunnelModuleProps = {
  mode?: 'stepper' | 'standalone';
  showQuoteRecap?: 'aside' | 'inline' | false;
  showTierSelect?: boolean;
  /** Per-vehicle tier selection table (fleet) instead of the single tier picker. */
  perAssetTierSelection?: boolean;
  policySections?: FormSectionsWithShortcuts;
  assetFields?: Array<FieldShortcut>;
  nonDelegatedDocuments?: string[];
  manualComponentsGroups?: ManualComponentsGroup[];
  recapLabelOverrides?: Record<string, string>;
  helper?: ReactNode;
};

const normalizePerilFieldValue = (val: unknown): unknown =>
  typeof val === 'boolean' ? String(val) : val;

export function QuoteFunnelModule({
  mode = 'stepper',
  showQuoteRecap = 'aside',
  showTierSelect = false,
  perAssetTierSelection = false,
  policySections = [],
  assetFields,
  nonDelegatedDocuments = [],
  manualComponentsGroups,
  recapLabelOverrides,
  helper,
}: QuoteFunnelModuleProps) {
  const stepper = useStepperContext();
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();

  const { data: asset, isLoading: isGetAssetLoading } = useCurrentActiveAsset({
    policyId: policy.id,
    branchId: workingBranch?.id,
  });

  const { data: assets, isLoading: isGetAssetsLoading } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
      count: MAX_FLEET_SIZE,
    }),
    select: data =>
      data.data
        .filter(asset => asset.status !== 'ASSET_STOPPED')
        .map(asset => ({
          ...asset,
          customFields: keyValueFieldsToObject(asset.customFields ?? []),
        })) as Asset[],
  });
  const assetIds = useMemo(() => assets?.map(asset => asset.id) ?? [], [assets]);

  const fleetMode = perAssetTierSelection && showTierSelect;
  const [fleetSelection, setFleetSelection, clearFleetSelectionDraft] = useFleetSelectionDraft(
    fleetSelectionDraftKey({ policyId: policy.id, branchId: workingBranch?.id }),
  );

  const [policyFieldsOverride, setPolicyFieldsOverride] = useState<CustomFieldDto[] | undefined>();
  const [assetFieldsOverride, setAssetFieldsOverride] = useState<CustomFieldDto[] | undefined>();
  const [policyCustomFieldsInputsOverride, setPolicyCustomFieldsInputsOverride] = useState<
    Record<string, unknown> | undefined
  >();

  const { config: policyFieldsConfig, isLoading: isPolicyFieldsConfigLoading } =
    useEntityFieldConfig({ entityType: 'policy', productId: policy.productId });
  const { config: assetFieldsConfig, isLoading: isAssetFieldsConfigLoading } = useEntityFieldConfig(
    { entityType: 'asset', productId: policy.productId },
  );

  const { data: quoteConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: data => data.quote,
  });

  const { data: needsDerogation, isLoading: isCheckingDerogationLoading } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      workingBranch?.id!,
      {},
      { action: 'QUOTE' },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(workingBranch?.id),
  });

  // needsDerogation checks if user is wholesale in case of non delegated quote.
  // Also show when branch type is RENEWAL.
  const showWaitingForQuote =
    (policy.status !== 'POLICY_QUOTED' || workingBranch?.branchType === 'RENEWAL') &&
    needsDerogation &&
    !isQuoteDerogationApproved(workingBranch);

  const { firmType } = useCurrentFirmType();

  const disableQuoteFields = shouldDisableQuoteFields(workingBranch, firmType);

  // Mirror form manual values into state so `useAvailablePricing` (called before `form`
  // because form defaults need `availablePricing.selectedTier`) can react to edits.
  const [availablePricingManualValues, setAvailablePricingManualValues] = useState<
    Record<string, number> | undefined
  >(undefined);

  const {
    data: availablePricing,
    isLoading: isPricingLoading,
    isFetching: isAvailablePricingFetching,
  } = useAvailablePricing(policy.id, {
    branchId: workingBranch?.id,
    policyCustomFields: policyFieldsOverride,
    assetCustomFields: assetFieldsOverride,
    policyCustomFieldsInputs: policyCustomFieldsInputsOverride,
    manualComponentValues: availablePricingManualValues,
  });

  const resolvedPolicySections: FormSections = useMemo(
    () =>
      resolveSectionsFieldShortcuts(policySections, policyFieldsConfig).filter(section =>
        section.fields.some(f => !f.hidden),
      ),
    [policySections, policyFieldsConfig],
  );

  const assetSections: FormSectionsWithShortcuts = useMemo(
    () =>
      !fleetMode && assetFields && assetFields.length > 0
        ? [{ title: t('quoteFunnel.assetSection.title'), fields: assetFields }]
        : [],
    [fleetMode, assetFields, t],
  );

  // Fleet reads the same asset config as filterable table columns: a form section would bind
  // every row to the single active asset.
  const fleetColumnFields = useMemo(
    () =>
      fleetMode ? toPayloadFields(resolveFieldShortcuts(assetFields ?? [], assetFieldsConfig)) : [],
    [fleetMode, assetFields, assetFieldsConfig],
  );

  const resolvedAssetSections: FormSections = useMemo(
    () =>
      resolveSectionsFieldShortcuts(assetSections, assetFieldsConfig)
        .map(section => ({
          ...section,
          fields: section.fields.filter(
            field => field.name !== 'startedAt' || field.disabled || asset?.createdOnBranch,
          ),
        }))
        .filter(section => section.fields.some(f => !f.hidden)),
    [assetSections, assetFieldsConfig, asset?.createdOnBranch],
  );

  const perilSections: FormSections = useMemo(() => {
    if (!showTierSelect || !availablePricing) return [];

    const paramNames = new Set<string>();
    for (const tier of availablePricing.tiers) {
      for (const peril of tier.perils ?? []) {
        for (const param of peril.customFields ?? []) {
          paramNames.add(param);
        }
      }
    }

    if (paramNames.size === 0) return [];

    return resolveSectionsFieldShortcuts(
      [{ fields: Array.from(paramNames) }],
      policyFieldsConfig,
    ).filter(section => section.fields.some(f => !f.hidden));
  }, [showTierSelect, policyFieldsConfig, availablePricing]);

  const policyFields = useMemo(
    () => buildAllFieldsFromSections(resolvedPolicySections),
    [resolvedPolicySections],
  );
  const resolvedAssetFields = useMemo(
    () => buildAllFieldsFromSections(resolvedAssetSections),
    [resolvedAssetSections],
  );
  const perilFields = useMemo(() => buildAllFieldsFromSections(perilSections), [perilSections]);

  const savedTier =
    availablePricing?.selectedTier ??
    asset?.quote?.options?.tier ??
    availablePricing?.defaultTier ??
    availablePricing?.tiers[0]?.tierId;

  // Seed the per-vehicle selection from each asset's saved tier (or the default) once loaded.
  useEffect(() => {
    if (!fleetMode || !assets || !savedTier) return;
    setFleetSelection(previous => {
      let changed = false;
      // Drop assets removed since the draft was stored, then seed the ones still missing.
      const next = Object.fromEntries(
        Object.entries(previous).filter(([assetId]) => {
          const stillExists = assets.some(({ id }) => id === assetId);
          changed = changed || !stillExists;
          return stillExists;
        }),
      );
      for (const fleetAsset of assets) {
        if (!next[fleetAsset.id]) {
          next[fleetAsset.id] = {
            tier: fleetAsset.quote?.options?.tier ?? savedTier,
            perils: {},
          };
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [fleetMode, assets, savedTier, setFleetSelection]);

  // Merge per peril, exactly like the table displays: an untouched peril keeps its tier's
  // included state — submitting less would quote cheaper than the price shown.
  const fleetPerilsForAsset = (assetId: string, tierId: string) => {
    const chosen = fleetSelection[assetId]?.perils ?? {};
    return (availablePricing?.tiers.find(({ tierId: id }) => id === tierId)?.perils ?? [])
      .filter(({ id, included }) => chosen[id]?.included ?? included)
      .map(({ id, options }) => ({ id, option: chosen[id]?.option ?? options?.[0] }));
  };

  const savedPerilIds = useMemo(() => {
    const tier = availablePricing?.tiers.find(t => t.tierId === savedTier);
    if (tier) {
      return tier.perils.filter(p => p.included).map(p => p.id);
    }
    return [];
  }, [availablePricing, savedTier]);

  const savedPerilParameters = useMemo(() => {
    const params: Record<string, unknown> = {};
    const tier = availablePricing?.tiers.find(t => t.tierId === savedTier);
    if (!tier) return params;
    for (const peril of tier.perils) {
      const isSelected = savedPerilIds.includes(peril.id);
      for (const paramName of peril.customFields ?? []) {
        const defaultValue = peril.defaultCustomFieldValues?.find(f => f.key === paramName)?.value;
        params[paramName] = isSelected || (defaultValue ?? false);
      }
    }
    return params;
  }, [availablePricing, savedTier, savedPerilIds]);

  const perilFieldDefaults = useMemo(() => {
    if (!availablePricing) return {};
    const defaults: Record<string, unknown> = {};
    for (const tier of availablePricing.tiers) {
      const isCurrentTier = tier.tierId === savedTier;
      for (const peril of tier.perils) {
        for (const cf of peril.customFields ?? []) {
          if (cf in defaults) continue;
          const raw = peril.defaultCustomFieldValues?.find(f => f.key === cf)?.value;
          const effective = typeof raw === 'boolean' && isCurrentTier ? peril.included : raw;
          const val = normalizePerilFieldValue(effective);
          if (val !== undefined) defaults[cf] = val;
        }
      }
    }
    return defaults;
  }, [availablePricing, savedTier]);

  // Only actual saved policy values (not buildFormValuesFromFields defaults) override perilFieldDefaults.
  const savedPolicyPerilValues = useMemo(() => {
    const cfMap = (policy.customFields ?? {}) as Record<string, unknown>;
    return Object.fromEntries(
      perilFields
        .map(f => [f.name, normalizePerilFieldValue(cfMap[f.name])] as [string, unknown])
        .filter(([, v]) => v !== undefined),
    );
  }, [policy.customFields, perilFields]);

  const manualComponentValueTypes = useManualComponentValueTypes(policy.productId, savedTier);
  const manualComponentsForPrefill = useManualComponentsConfig(policy.productId, savedTier);

  const existingManualValues = useMemo(
    () => fromSavedManualComponents(policy.quote?.components ?? [], manualComponentsForPrefill),
    [policy.quote, manualComponentsForPrefill],
  );

  const requiredManualComponentIds = useMemo(
    () =>
      manualComponentsForPrefill
        .filter(c => c.required && isComponentEditableBy(c, firmType))
        .map(c => c.id),
    [manualComponentsForPrefill, firmType],
  );

  const baseResolver = getResolver([...policyFields, ...resolvedAssetFields, ...perilFields]);
  const resolver: typeof baseResolver = async (values, context = {}) => {
    const result = await baseResolver(values, context);
    const errors = { ...result.errors } as Record<string, unknown>;
    const manual = (values.manualComponents ?? {}) as Record<string, unknown>;
    const manualErrors: Record<string, { type: string; message: string }> = {};
    for (const id of requiredManualComponentIds) {
      const v = manual[id];
      if (v === undefined || v === null || v === '') {
        manualErrors[id] = { type: 'required', message: 'Ce champ est requis' };
      }
    }
    if (Object.keys(manualErrors).length > 0) {
      errors.manualComponents = manualErrors;
    }
    return {
      values: Object.keys(errors).length === 0 ? result.values : ({} as typeof result.values),
      errors: errors as typeof result.errors,
    };
  };

  const form = useForm({
    mode: 'all',
    values: {
      tier: savedTier,
      ...buildFormValuesFromFields(policyFields, policy),
      ...buildFormValuesFromFields(resolvedAssetFields, asset),
      ...(showTierSelect ? { ...perilFieldDefaults, ...savedPolicyPerilValues } : {}),
      manualComponents: existingManualValues,
    } as Record<string, unknown>,
    resetOptions: { keepDirtyValues: true },
    resolver,
  });

  const formValues = showTierSelect ? form.watch() : undefined;

  // Peril fields only exist once available-pricing resolves, so they are absent from the form's
  // initial defaultValues and land as "dirty" — which keepDirtyValues then pins to empty forever.
  useEffect(() => {
    if (!showTierSelect) return;
    for (const [name, value] of Object.entries(savedPolicyPerilValues)) {
      if (form.formState.touchedFields[name]) continue;
      if (form.getValues(name) !== '' && form.getValues(name) !== undefined) continue;
      form.setValue(name, value);
    }
  }, [showTierSelect, savedPolicyPerilValues]);

  const activeTier = showTierSelect ? (formValues?.tier as string | undefined) : savedTier;

  const activeTierPerils = useMemo(() => {
    const tier = availablePricing?.tiers.find(t => t.tierId === activeTier);
    return tier?.perils ?? [];
  }, [availablePricing, activeTier]);

  const policyFieldNames = useMemo(() => policyFields.map(f => f.name), [policyFields]);
  const assetFieldNames = useMemo(
    () => resolvedAssetFields.map(f => f.name),
    [resolvedAssetFields],
  );
  const watchedPolicyValues = form.watch(policyFieldNames);
  const watchedAssetValues = form.watch(assetFieldNames);

  useEffect(() => {
    if (policyFields.length === 0) {
      setPolicyFieldsOverride(undefined);
      return;
    }
    const { customFields } = buildPayloadFromFields(policyFields, form.getValues()) as {
      customFields?: CustomFieldDto[];
    };
    setPolicyFieldsOverride(customFields?.length ? customFields : undefined);
  }, [policyFields, JSON.stringify(watchedPolicyValues)]);

  useEffect(() => {
    if (resolvedAssetFields.length === 0) {
      setAssetFieldsOverride(undefined);
      return;
    }
    const { customFields } = buildPayloadFromFields(resolvedAssetFields, form.getValues()) as {
      customFields?: CustomFieldDto[];
    };
    setAssetFieldsOverride(customFields?.length ? customFields : undefined);
  }, [resolvedAssetFields, JSON.stringify(watchedAssetValues)]);

  const activePolicyParameters = useMemo(() => {
    if (!showTierSelect) return {};
    const configDefaults = Object.fromEntries(
      policyFieldsConfig
        .filter(f => f.defaultValue !== undefined)
        .map(f => [f.key, f.defaultValue]),
    );
    const params: Record<string, unknown> = { ...configDefaults };
    for (const field of policyFields) {
      params[field.name] = formValues?.[field.name] ?? params[field.name] ?? false;
    }
    return params;
  }, [showTierSelect, policyFields, formValues, policyFieldsConfig]);

  const activePerilParameters = useMemo(() => {
    if (!showTierSelect) return savedPerilParameters;
    const params: Record<string, unknown> = {};
    for (const field of perilFields) {
      params[field.name] = formValues?.[field.name] ?? false;
    }
    return params;
  }, [showTierSelect, savedPerilParameters, perilFields, formValues]);

  const policyCustomFieldsInputs = useMemo(
    () => ({ ...activePolicyParameters, ...activePerilParameters }),
    [activePolicyParameters, activePerilParameters],
  );

  useEffect(() => {
    const next =
      !showTierSelect || (perilFields.length === 0 && policyFields.length === 0)
        ? undefined
        : policyCustomFieldsInputs;
    setPolicyCustomFieldsInputsOverride(prev => (isEqual(prev, next) ? prev : next));
  }, [showTierSelect, perilFields.length, policyFields.length, policyCustomFieldsInputs]);

  const activePerilIds = useMemo(() => {
    if (!showTierSelect || !availablePricing) return savedPerilIds;
    const tier = availablePricing.tiers.find(t => t.tierId === activeTier);
    if (!tier) return savedPerilIds;
    return tier.perils.map(p => p.id);
  }, [showTierSelect, availablePricing, activeTier, savedPerilIds]);

  const watchedManualComponents = useWatch({
    control: form.control,
    name: 'manualComponents',
  }) as ManualComponentFormMap | undefined;

  const activeManualComponentValues = useMemo(
    () => toManualComponentPayload(watchedManualComponents, manualComponentValueTypes),
    [watchedManualComponents, manualComponentValueTypes],
  );

  // Key on the serialized snapshot so the effect only fires on value changes, not identity changes.
  const activeManualComponentValuesKey = useMemo(
    () => JSON.stringify(activeManualComponentValues),
    [activeManualComponentValues],
  );
  useEffect(() => {
    setAvailablePricingManualValues(
      Object.keys(activeManualComponentValues).length > 0 ? activeManualComponentValues : undefined,
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the JSON snapshot above
  }, [activeManualComponentValuesKey]);

  const {
    data: computedPricing,
    isLoading: isComputingPricing,
    isFetching: isComputingPricingFetching,
  } = useComputePricing({
    policyId: policy.id,
    branchId: workingBranch?.id,
    tierId: activeTier,
    selectedPerilIds: activePerilIds,
    policyCustomFieldsInputs,
    manualComponentValues: activeManualComponentValues,
  });

  const calculatedQuotes = useMemo(() => {
    if (!availablePricing) return [];
    return availablePricing.tiers.map(t => {
      if (t.tierId === activeTier && computedPricing) {
        return {
          tier: t.tierId,
          excess: 'DEFAULT',
          quote: computedPricing,
          breakdown: t.breakdown,
        };
      }
      return {
        tier: t.tierId,
        excess: 'DEFAULT',
        quote: t.pricing,
        breakdown: t.breakdown,
      };
    });
  }, [availablePricing, activeTier, computedPricing]);

  const { mutateAsync: updatePolicy } = usePolicyControllerEditPolicy({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updatePolicyFees } = usePolicyControllerSetPolicyFees({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updateAsset } = useAssetControllerEditAsset({
    client: { client: clientWithFork },
  });

  const { mutateAsync: quotePolicy } = usePolicyControllerQuotePolicy({
    client: { client: clientWithFork },
    mutation: {
      onError: error => {
        const detailedError = error as { errorResponse?: { data?: { code?: string } } };

        if (detailedError?.errorResponse?.data?.code === 'NO_CHANGES_TO_QUOTE') {
          toast({
            variant: 'warning',
            title: t('quoteFunnel.noChangesToQuote.title'),
            description: t('quoteFunnel.noChangesToQuote.description'),
          });

          if (mode === 'stepper') {
            stepper.next();
          }

          return;
        }

        throw error;
      },
    },
  });

  const { mutateAsync: updatePolicyQuote } = usePolicyControllerUpdatePolicyQuote({
    client: { client: clientWithFork },
  });

  const { mutateAsync: savePricingSelection } = useSavePricingSelection(policy.id);

  const chosenPerils = useMemo(() => {
    return activePerilIds.map(id => ({ id }));
  }, [activePerilIds]);

  // Sections resolved against a config that has not landed yet would render an empty form.
  const areQueriesLoading =
    isGetAssetLoading ||
    isGetAssetsLoading ||
    isPricingLoading ||
    isPolicyFieldsConfigLoading ||
    isAssetFieldsConfigLoading;

  if (areQueriesLoading || isCheckingDerogationLoading || showWaitingForQuote === undefined) {
    return <LoadingCard />;
  }

  if (!workingBranch || !asset || !availablePricing) {
    return null;
  }

  if (showWaitingForQuote && quoteConfig && assets) {
    return (
      <WaitingForQuoteCard
        policy={policy}
        asset={asset}
        assets={assets}
        branch={workingBranch}
        quoteConfig={quoteConfig}
        policySections={policySections}
        assetSections={assetSections}
      />
    );
  }

  const onSubmit = form.handleSubmit(async data => {
    const { dirtyFields } = form.formState;

    let mutationBranchId: string = workingBranch.id;

    if (policyFields.length > 0 && areFieldsDirty(policyFields, dirtyFields)) {
      const { fees, ...policyBody } = buildPayloadFromFields(
        policyFields,
        data,
        dirtyFields,
      ) as Partial<EditPolicyInputBody> & { fees?: BrokerageFeeOnPolicy[] };

      if (!isEmpty(policyBody)) {
        const response = await updatePolicy({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: policyBody,
        });
        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }

      if (fees !== undefined) {
        const response = await updatePolicyFees({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { fees },
        });
        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    if (
      resolvedAssetFields.length > 0 &&
      areFieldsDirty(resolvedAssetFields, dirtyFields) &&
      asset
    ) {
      const assetPayload = buildPayloadFromFields(resolvedAssetFields, data, dirtyFields);
      const response = await updateAsset({
        assetId: asset.id,
        params: { branchId: mutationBranchId },
        data: assetPayload,
      });

      const newBranchId = getNewBranchIdAfterFork(response);
      if (newBranchId) {
        mutationBranchId = newBranchId;
      }
    }

    if (showTierSelect && !fleetMode) {
      const { customFields: policyCustomFields = [] } = buildPayloadFromFields(
        perilFields,
        data,
      ) as { customFields?: CustomFieldDto[] };

      const selectionResult = await savePricingSelection({
        tierId: activeTier as string,
        policyCustomFields: policyCustomFields.length > 0 ? policyCustomFields : undefined,
        branchId: mutationBranchId,
      });
      if (selectionResult.newBranchId) {
        mutationBranchId = selectionResult.newBranchId;
      }
    }

    // The form is prefilled with every manualComponent value (including IDs
    // owned by other firm types). Restrict the payload to IDs the current
    // firmType is allowed to edit so retail can't re-send wholesale's IDs.
    // The backend overlays missing IDs from the existing quote, so dropping
    // them here is safe.
    const editableManualComponentIds = new Set(
      manualComponentsForPrefill.filter(c => isComponentEditableBy(c, firmType)).map(c => c.id),
    );
    const allSubmittedManualValues = toManualComponentPayload(
      form.getValues('manualComponents') as ManualComponentFormMap | undefined,
      manualComponentValueTypes,
    );
    const submittedManualValues: Record<string, number> = {};
    for (const [id, value] of Object.entries(allSubmittedManualValues)) {
      if (editableManualComponentIds.has(id)) submittedManualValues[id] = value;
    }
    const manualComponentValuesDto =
      Object.keys(submittedManualValues).length > 0 ? { values: submittedManualValues } : undefined;

    // Re-quote on a QUOTED policy goes through updatePolicyQuote so the
    // approved derogation on the current branch survives — quotePolicy would
    // 409 and the auto-fork retry would land on a branch that doesn't carry
    // the derogation.
    const tierOrPerilDirty =
      Boolean(dirtyFields.tier) ||
      perilFields.some(f => Boolean((dirtyFields as Record<string, unknown>)[f.name]));
    // Fleet mode always re-quotes: per-asset tiers live outside the form's dirty tracking.
    const isReQuote = !fleetMode && policy.status === 'POLICY_QUOTED' && !tierOrPerilDirty;

    let newBranchId: string | undefined;
    if (isReQuote) {
      const response = await updatePolicyQuote({
        policyId: policy.id,
        params: { branchId: mutationBranchId },
        data: { manualComponentValues: manualComponentValuesDto },
      });
      newBranchId = getNewBranchIdAfterFork(response);
    } else {
      const response = await quotePolicy({
        policyId: policy.id,
        params: { branchId: mutationBranchId },
        data: {
          options: assetIds.map(assetId => {
            const assetTier = fleetMode
              ? (fleetSelection[assetId]?.tier ?? (activeTier as string))
              : (activeTier as string);
            return {
              assetId,
              excess: 'DEFAULT',
              tier: assetTier,
              perils: fleetMode ? fleetPerilsForAsset(assetId, assetTier) : chosenPerils,
            };
          }),
          manualComponentValues: manualComponentValuesDto,
        },
      });
      newBranchId = getNewBranchIdAfterFork(response);
    }

    if (newBranchId) {
      mutationBranchId = newBranchId;
    }

    // Each asset now carries its tier server-side; a leftover draft would resurrect stale picks.
    clearFleetSelectionDraft();

    if (mode === 'stepper') {
      stepper.next(
        mutationBranchId !== workingBranch.id ? { branchId: mutationBranchId } : undefined,
      );
    }
  });

  const recapSection = showQuoteRecap ? (
    <QuoteRecapModule labelOverrides={recapLabelOverrides} />
  ) : null;

  const nonDelegatedDocumentsSection =
    Boolean(nonDelegatedDocuments.length) && firmType === 'WHOLESALE' ? (
      <DocumentsUploadPolicyModule
        displayContext="broker"
        documentsLists={nonDelegatedDocuments.map(category => ({
          category,
          documentType: category,
        }))}
        categoryType="nonDelegatedDocuments"
        useBranchId={Boolean(workingBranch.id)}
        authorizedFormats={{ 'application/pdf': ['.pdf'] }}
      />
    ) : null;

  const mainSection = (
    <>
      {resolvedAssetSections.length > 0 && (
        <FormBuilderSections sections={resolvedAssetSections} externalData={asset} />
      )}
      {resolvedPolicySections.length > 0 && (
        <FormBuilderSections
          sections={resolvedPolicySections}
          externalData={policy}
          disabled={disableQuoteFields}
        />
      )}
      {availablePricing &&
        (fleetMode ? (
          <FleetTierTable
            assets={assets ?? []}
            tiers={availablePricing.tiers}
            productId={policy.productId}
            value={fleetSelection}
            onChange={setFleetSelection}
            defaultTier={savedTier ?? availablePricing.defaultTier}
            fields={fleetColumnFields}
            disabled={isComputingPricingFetching}
          />
        ) : (
          <PricingTierSelect
            tiers={availablePricing.tiers}
            selectedTierPricing={computedPricing}
            productId={policy.productId}
            isPricingLoading={isAvailablePricingFetching}
            disabled={isComputingPricingFetching || !showTierSelect}
            readOnly={!showTierSelect}
          />
        ))}
      {showTierSelect && !fleetMode && perilFields.length > 0 && (
        <PerilOptionsCard
          productId={policy.productId}
          perils={activeTierPerils}
          perilFields={perilFields}
        />
      )}
      <ManualComponentsCard
        productId={policy.productId}
        activeTierId={activeTier}
        firmType={firmType}
        groups={manualComponentsGroups}
      />
      {nonDelegatedDocumentsSection}
    </>
  );

  return (
    <QuoteContext.Provider
      value={{
        policy,
        asset,
        calculatedQuotes,
        isLoading: isComputingPricing || isComputingPricingFetching,
      }}
    >
      <Form {...form}>
        <form className="flex flex-col gap-4" onSubmit={e => e.preventDefault()}>
          {disableQuoteFields && <QuoteDerogationLockNotice />}
          {showQuoteRecap === 'aside' ? (
            <Stack className="xl:flex-row">
              <div className="w-full xl:w-5/7">
                <Stack>{mainSection}</Stack>
              </div>
              <Stack className="w-full xl:w-2/7 self-start sticky top-4">
                {recapSection}
                {helper && (
                  <Card>
                    <CardContent className="pt-6">{helper}</CardContent>
                  </Card>
                )}
              </Stack>
            </Stack>
          ) : showQuoteRecap === 'inline' ? (
            <Stack>
              {mainSection}
              {recapSection}
            </Stack>
          ) : null}

          <FormBuilderFooter
            mode={mode}
            showPreviousButton={true}
            form={form}
            onSubmit={onSubmit}
          />
        </form>
      </Form>
    </QuoteContext.Provider>
  );
}
